import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  type MonthReportPublic,
  monthlyReportsComputeMutation,
  monthlyReportsForMonthOptions,
  monthlyReportsForMonthQueryKey,
} from "@/lib/api";

/** The first of the month `offset` months back, as the API takes it. */
export function monthIso(offset: number): string {
  const d = new Date();
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + offset, 1))
    .toISOString()
    .slice(0, 10);
}

export function monthLabel(iso: string): string {
  return new Date(`${iso}T00:00:00Z`).toLocaleDateString(undefined, {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });
}

export function useMonthReports(month: string) {
  return useQuery(monthlyReportsForMonthOptions({ query: { month } }));
}

export function useComputeMonth(month: string) {
  const queryClient = useQueryClient();
  return useMutation({
    ...monthlyReportsComputeMutation(),
    onSuccess: (data) => {
      queryClient.setQueryData(monthlyReportsForMonthQueryKey({ query: { month } }), data);
      toast.success(`Figures for ${monthLabel(month)} are up to date.`);
    },
  });
}

const COLUMNS: { key: keyof MonthReportPublic; label: string }[] = [
  { key: "orgName", label: "Client" },
  { key: "team", label: "Team" },
  { key: "peoplePosting", label: "People posting" },
  { key: "posts", label: "Posts" },
  { key: "postReactions", label: "Post reactions" },
  { key: "postComments", label: "Post comments" },
  { key: "postShares", label: "Post shares" },
  { key: "pillarsUsed", label: "Pillars used" },
  { key: "pillarsTotal", label: "Pillars" },
  { key: "mentions", label: "Mentions" },
  { key: "pageConnected", label: "Page connected" },
  { key: "pageFollowersEnd", label: "Page followers" },
  { key: "pageFollowerChange", label: "Follower change" },
  { key: "pageImpressions", label: "Page impressions" },
  { key: "pageViews", label: "Page views" },
  { key: "pagePosts", label: "Page posts" },
  { key: "pageReactions", label: "Page reactions" },
  { key: "pageComments", label: "Page comments" },
  { key: "pageShares", label: "Page shares" },
];

export { COLUMNS };

/** The month's table as a CSV data URL, one row per client. */
export function csvDataUrl(month: string, rows: MonthReportPublic[]): string {
  const cell = (v: unknown) => {
    const text = v === null || v === undefined ? "" : String(v);
    return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
  };
  const lines = [
    ["Month", ...COLUMNS.map((c) => c.label)].map(cell).join(","),
    ...rows.map((r) => [month, ...COLUMNS.map((c) => cell(r[c.key]))].join(",")),
  ];
  return `data:text/csv;charset=utf-8,${encodeURIComponent(lines.join("\n"))}`;
}
